import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useGenerateApiKey } from "../hooks/useGenerateApiKey";
import { useGetApiKeys } from "../hooks/useGetApiKey";
import { useRevokeApiKey } from "../hooks/useRevokeApiKey";
import type { IntegrationModuleChip } from "../types";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import { useAuth } from "@/lib/hooks/useAuth";

type GenerateApiKeyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integrationName: string;
  integrationDisplayName: string;
  modules: IntegrationModuleChip[];
};

export function GenerateApiKeyDialog({
  open,
  onOpenChange,
  integrationName,
  integrationDisplayName,
  modules,
}: GenerateApiKeyDialogProps) {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);

  const generateMutation = useGenerateApiKey();
  const revokeMutation = useRevokeApiKey();
  const apiKeysQuery = useGetApiKeys(integrationName, tenantId, open);

  const apiKeys = apiKeysQuery.data ?? [];

  // Per-module scope breakdown: which module grants which scopes (active modules that have scopes).
  const modulesWithScopes = modules.filter((m) => m.isActive && m.scopes.length > 0);

  const handleGenerate = async () => {
    if (!tenantId) return;
    try {
      await generateMutation.mutateAsync({ integrationName, tenantId });
    } catch {
      // Error handled by the mutation
    }
  };

  const handleCopy = (id: string, apiKey: string) => {
    navigator.clipboard.writeText(apiKey);
    setCopiedId(id);
    setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
  };

  const handleRevokeConfirm = async () => {
    if (!tenantId || !revokeTargetId) return;
    try {
      await revokeMutation.mutateAsync({ integrationName, apiKeyId: revokeTargetId, tenantId });
    } catch {
      // Error handled by the mutation
    } finally {
      setRevokeTargetId(null);
    }
  };

  const handleClose = () => {
    setCopiedId(null);
    setRevokeTargetId(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("integrations.apiKey.title", "API Keys")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "integrations.apiKey.description",
              "Manage API keys for {{integration}}",
              { integration: integrationDisplayName }
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {apiKeysQuery.isLoading ? null : apiKeys.length > 0 ? (
            <div className="divide-y rounded-lg border">
              {apiKeys.map((key) => (
                <div key={key.id} className="flex items-center gap-2 px-3 py-2">
                  <input
                    type="text"
                    readOnly
                    value={key.apiKey}
                    className="w-full rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => handleCopy(key.id, key.apiKey)}
                  >
                    {copiedId === key.id ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="shrink-0"
                    disabled={revokeMutation.isPending || !tenantId}
                    onClick={() => setRevokeTargetId(key.id)}
                  >
                    {t("integrations.apiKey.revoke", "Revoke")}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center">
              <p className="text-sm text-muted-foreground mb-1">
                {t("integrations.apiKey.noKeys", "No API keys have been generated yet.")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("integrations.apiKey.noKeysHint", "Click 'Generate' below to create one.")}
              </p>
            </div>
          )}

          {/* Per-module scope breakdown: what each key can do, by module. */}
          {modulesWithScopes.length > 0 && (
            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-semibold">
                {t("integrations.apiKey.scopes", "Scopes")}
              </h3>
              <div className="divide-y rounded-lg border">
                {modulesWithScopes.map((module) => (
                  <div key={module.moduleName} className="flex items-start justify-between gap-4 px-3 py-2">
                    <span className="text-sm font-medium">
                      {t(`integrations.modules.names.${module.moduleName}`, module.moduleName)}
                    </span>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {module.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t("common.close", "Close")}
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={apiKeysQuery.isLoading || !tenantId}
            loading={generateMutation.isPending}
          >
            {t("common.generate", "Generate")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={revokeTargetId !== null} onOpenChange={(o) => { if (!o) setRevokeTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("integrations.apiKey.revokeConfirmTitle", "Revoke API Key?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "integrations.apiKey.revokeConfirmDescription",
                "Are you sure you want to revoke this API key? This action cannot be undone. Any applications using this key will lose access immediately."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revokeMutation.isPending}>
              {t("common.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleRevokeConfirm}
              loading={revokeMutation.isPending}
            >
              {t("integrations.apiKey.revoke", "Revoke")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
